import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// --- Custom navy/gold icons (SVG data URI, no external files needed) ---
function makeIcon({ color, size = 22, pulse = false }) {
  const html = `
    <div style="position:relative;width:${size}px;height:${size}px;">
      ${
        pulse
          ? `<span style="position:absolute;inset:-6px;border-radius:9999px;background:${color};opacity:0.35;animation:dvl-marker-pulse 1.6s ease-out infinite;"></span>`
          : ""
      }
      <span style="position:absolute;inset:0;border-radius:9999px;background:${color};border:2px solid #F8FAFC;box-shadow:0 0 0 3px rgba(10,17,40,0.9),0 8px 18px rgba(0,0,0,.5);"></span>
      <span style="position:absolute;inset:5px;border-radius:9999px;background:#0A1128;"></span>
    </div>`;
  return L.divIcon({
    className: "dvl-marker",
    html,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
}

const ICON_ORIGIN = makeIcon({ color: "#C5A059", size: 20 });
const ICON_CHECKPOINT = makeIcon({ color: "#D4AF37", size: 18 });
const ICON_LATEST = makeIcon({ color: "#F3E5AB", size: 26, pulse: true });
const ICON_DESTINATION = makeIcon({ color: "#10B981", size: 20 });

// Static polyline styles — hoisted so each render doesn't allocate new objects
// (avoids unnecessary Leaflet layer re-renders).
const PLANNED_ROUTE_STYLE = {
  color: "#D4AF37",
  weight: 1.5,
  opacity: 0.35,
  dashArray: "4 8",
};
const COMPLETED_ROUTE_STYLE = {
  color: "#F3E5AB",
  weight: 2.5,
  opacity: 0.9,
  className: "dvl-route-line",
};
const MAP_STYLE = { height: "100%", width: "100%", background: "#0A1128" };

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView(points[0], 5);
      return;
    }
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [50, 50], maxZoom: 6 });
  }, [points, map]);
  return null;
}

export default function RouteMap({ pkg }) {
  const containerRef = useRef(null);
  const [mounted, setMounted] = useState(false);
  // Deps intentionally empty: mount-only sentinel used to defer MapContainer
  // creation past the first render. setMounted is stable per React.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setMounted(true);
  }, []);

  const routePoints = useMemo(() => {
    if (!pkg) return [];
    const pts = [];
    if (pkg.origin_coords) pts.push({ pos: pkg.origin_coords, label: pkg.origin, kind: "origin" });
    (pkg.checkpoints || []).forEach((c, idx) => {
      if (c.coords) {
        pts.push({
          pos: c.coords,
          label: c.location,
          status: c.status,
          notes: c.notes,
          timestamp: c.timestamp,
          kind: "checkpoint",
          isLatest: idx === (pkg.checkpoints.length - 1),
        });
      }
    });
    const currentStatus = (pkg.current_status || "").toLowerCase();
    const isDelivered = currentStatus.includes("deliver");
    if (pkg.destination_coords) {
      pts.push({
        pos: pkg.destination_coords,
        label: pkg.destination,
        kind: isDelivered ? "delivered" : "destination",
      });
    }
    // Deduplicate consecutive identical positions
    return pts.filter((p, i, arr) => i === 0 || (arr[i - 1].pos[0] !== p.pos[0] || arr[i - 1].pos[1] !== p.pos[1]));
  }, [pkg]);

  const positions = useMemo(() => routePoints.map((p) => p.pos), [routePoints]);

  if (!pkg || routePoints.length === 0) return null;

  return (
    <div
      ref={containerRef}
      data-testid="route-map"
      className="rounded-2xl overflow-hidden border border-amber-500/25 shadow-xl shadow-black/40 relative"
      style={{ height: 420 }}
    >
      {mounted && (
      <MapContainer
        key={pkg.tracking_id}
        center={positions[0]}
        zoom={3}
        scrollWheelZoom={false}
        style={MAP_STYLE}
        attributionControl={false}
      >
        <TileLayer
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxZoom={19}
        />

        {/* Dashed underlay for the full planned route */}
        <Polyline positions={positions} pathOptions={PLANNED_ROUTE_STYLE} />

        {/* Solid, animated completed segment (up to latest checkpoint) */}
        {routePoints.length > 1 && (
          <Polyline
            positions={positions.slice(
              0,
              routePoints.findIndex((p) => p.isLatest) >= 0
                ? routePoints.findIndex((p) => p.isLatest) + 1
                : positions.length
            )}
            pathOptions={COMPLETED_ROUTE_STYLE}
          />
        )}

        {routePoints.map((p, i) => {
          let icon = ICON_CHECKPOINT;
          if (p.kind === "origin") icon = ICON_ORIGIN;
          else if (p.kind === "destination" || p.kind === "delivered") icon = ICON_DESTINATION;
          else if (p.isLatest) icon = ICON_LATEST;
          return (
            <Marker
              key={`${p.pos[0]}-${p.pos[1]}-${i}`}
              position={p.pos}
              icon={icon}
            >
              <Popup className="dvl-popup">
                <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", minWidth: 180 }}>
                  <div
                    style={{
                      fontSize: 10,
                      letterSpacing: 2,
                      textTransform: "uppercase",
                      color: "#D4AF37",
                      marginBottom: 4,
                      fontFamily: "'JetBrains Mono', monospace",
                    }}
                  >
                    {p.kind === "origin"
                      ? "Origin"
                      : p.kind === "destination"
                      ? "Destination"
                      : p.kind === "delivered"
                      ? "Delivered"
                      : p.status || "Checkpoint"}
                  </div>
                  <div style={{ fontWeight: 600, color: "#0A1128" }}>{p.label}</div>
                  {p.notes && (
                    <div style={{ marginTop: 6, fontSize: 12, color: "#334155" }}>{p.notes}</div>
                  )}
                  {p.timestamp && (
                    <div
                      style={{
                        marginTop: 6,
                        fontSize: 10,
                        color: "#64748B",
                        fontFamily: "'JetBrains Mono', monospace",
                      }}
                    >
                      {new Date(p.timestamp).toLocaleString("en-GB")}
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}

        <FitBounds points={positions} />
      </MapContainer>
      )}

      {/* Legend overlay */}
      <div
        className="absolute top-3 left-3 bg-[#0A1128]/85 backdrop-blur-md border border-amber-500/30 rounded-lg px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-slate-300 space-y-1 pointer-events-none z-[400]"
        data-testid="map-legend"
      >
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-amber-600 border border-slate-50" /> Origin
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-amber-200 border border-slate-50" /> Current
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 border border-slate-50" /> Destination
        </div>
      </div>
    </div>
  );
}
